/**
 * Layer 1: Primitive color tokens.
 * Raw palettes (25–800). Single source of truth for hex values in JavaScript.
 *
 * Use cases:
 * 1. Feeds themes.js — THEMES are built from these palettes (createTheme uses brand, blue, neutral, etc.).
 * 2. JS usage — Charts, inline styles, or design tooling can import PRIMITIVE_PALETTES for raw palette access.
 * 3. One place for palette hex in code — Change a value here and themes.js stays in sync; themes.scss is maintained separately (or could be generated from themes.js).
 *
 * Components should not use these in CSS; use semantic tokens via var(--color-*) from themes.scss.
 */

/** @typedef {25|40|50|75|100|150|175|200|300|400|450|500|550|600|650|675|700|800} Shade */

/** @typedef {'brand'|'blue'|'red'|'yellow'|'orange'|'green'|'neutral'} PaletteName */

/** @typedef {Record<Shade, string>} Palette */

export const PRIMITIVE_PALETTES = {
  brand: {
    800: '#004E48',
    700: '#00766D',
    600: '#009D91',
    500: '#00C4B5',
    400: '#33D0C4',
    300: '#66DCD3',
    200: '#99E7E1',
    100: '#CCF3F0',
    50: '#E5F9F8',
  },
  blue: {
    800: '#1B3266',
    700: '#284C98',
    600: '#3665CB',
    500: '#437EFE',
    400: '#6998FE',
    300: '#8EB2FE',
    200: '#B4CBFF',
    100: '#D9E5FF',
    50: '#ECF2FF',
  },
  red: {
    800: '#7C3536',
    700: '#A53A3B',
    650: '#E05B4F',
    600: '#CF4040',
    500: '#F94545',
    400: '#FA6A6A',
    300: '#FB8F8F',
    200: '#FDB5B5',
    100: '#FEDADA',
    50: '#FEECEC',
  },
  yellow: {
    800: '#7E5E22',
    700: '#A9781C',
    650: '#E29A2E',
    600: '#D49217',
    500: '#FFAC12',
    400: '#FFBD41',
    300: '#FFCD71',
    200: '#FFDEA0',
    100: '#FFEED0',
    50: '#FFF7E7',
    25: '#FDF5E9',
  },
  orange: {
    800: '#642911',
    700: '#973D1A',
    600: '#C95222',
    500: '#FB662B',
    400: '#FC8555',
    300: '#FDA380',
    200: '#FDC2AA',
    100: '#FEE0D5',
    50: '#FFF0EA',
  },
  green: {
    800: '#094C28',
    700: '#0E733B',
    675: '#238C73',
    650: '#2FA98C',
    600: '#12994F',
    500: '#17BF63',
    450: '#3FBFA1',
    400: '#45CC82',
    300: '#74D9A1',
    200: '#A2E5C1',
    100: '#D1F2E0',
    75: '#DFF4EE',
    50: '#E8F9EF',
    25: '#F1FAF7',
  },
  neutral: {
    800: '#121314',
    700: '#282A2C',
    650: '#4A5760',
    600: '#4F5153',
    550: '#7C8A93',
    500: '#707274',
    450: '#A9B4BB',
    400: '#B6B9BB',
    300: '#C3C7C6',
    200: '#D3D7D6',
    175: '#D2D9DD',
    150: '#E4E8EA',
    100: '#E7ECEB',
    75: '#F4F6F580',
    50: '#F4F6F5',
    40: '#F0F4F5',
    25: '#F7F9FA',
  },
  black: {
    50: '#00000080',
  },
};
